import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Select,
  Space,
  Switch,
  message,
  Typography,
  type FormInstance,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import complianceService, { type AspConnectionDto } from '@/services/compliance.service';
import { API_BASE_URL, apiOriginForStaticAssets } from '@/config/apiBaseUrl';
import { getCompanyIdFromStorage } from '@/utils/jwtPermissions';

const { Text } = Typography;

const AIGENTRIX_DEFAULT_API_BASE = 'https://app.aigentrix.ai';
const AIGENTRIX_DEFAULT_ENVIRONMENT = 'SANDBOX';

/** Public webhook URL Aigentrix should call for this ORDAN company. */
function defaultAigentrixWebhookUrl(companyId: string): string {
  const id = String(companyId || '').trim();
  if (!id) return '';
  const apiBase = API_BASE_URL.replace(/\/$/, '');
  if (apiBase.startsWith('http://') || apiBase.startsWith('https://')) {
    return `${apiBase}/webhooks/aigentrix/${id}`;
  }
  const origin =
    apiOriginForStaticAssets() ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const path = apiBase.startsWith('/') ? apiBase : `/${apiBase}`;
  return origin ? `${origin}${path}/webhooks/aigentrix/${id}` : `${path}/webhooks/aigentrix/${id}`;
}

/** Fill empty ASP fields with known Aigentrix defaults (does not overwrite user/saved values). */
function applyAigentrixDefaults(form: FormInstance): void {
  const current = form.getFieldsValue([
    'environment',
    'api_base_url',
    'webhook_url',
  ]) as {
    environment?: string;
    api_base_url?: string;
    webhook_url?: string;
  };
  const companyId = getCompanyIdFromStorage();
  const patch: Record<string, string> = {};
  if (!String(current.environment || '').trim()) {
    patch.environment = AIGENTRIX_DEFAULT_ENVIRONMENT;
  }
  if (!String(current.api_base_url || '').trim()) {
    patch.api_base_url = AIGENTRIX_DEFAULT_API_BASE;
  }
  if (!String(current.webhook_url || '').trim()) {
    const webhook = defaultAigentrixWebhookUrl(companyId);
    if (webhook) patch.webhook_url = webhook;
  }
  if (Object.keys(patch).length) {
    form.setFieldsValue(patch);
  }
}

/** Configure Aigentrix (TronStride FZC) ASP connection for ORDAN UAE e-invoicing. */
const AspConnectionPanel: React.FC = () => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [active, setActive] = useState<AspConnectionDto | null>(null);
  const enabled = Form.useWatch('enabled', form);

  const { data: connections = [], isLoading } = useQuery({
    queryKey: ['asp-connections'],
    queryFn: () => complianceService.listAspConnections(),
  });

  useEffect(() => {
    const row =
      connections.find((c) => c.provider_code === 'aigentrix' && c.enabled) ||
      connections.find((c) => c.provider_code === 'aigentrix') ||
      connections[0] ||
      null;
    setActive(row);
    if (row) {
      form.setFieldsValue({
        enabled: row.enabled,
        environment: row.environment || AIGENTRIX_DEFAULT_ENVIRONMENT,
        api_base_url: row.api_base_url || (row.enabled ? AIGENTRIX_DEFAULT_API_BASE : ''),
        external_company_id: row.external_company_id || '',
        peppol_participant_id: row.peppol_participant_id || '',
        webhook_url:
          row.webhook_url ||
          (row.enabled ? defaultAigentrixWebhookUrl(getCompanyIdFromStorage()) : ''),
        notes: row.notes || '',
        api_key: '',
      });
    } else {
      form.setFieldsValue({
        enabled: false,
        environment: AIGENTRIX_DEFAULT_ENVIRONMENT,
        api_base_url: AIGENTRIX_DEFAULT_API_BASE,
        webhook_url: defaultAigentrixWebhookUrl(getCompanyIdFromStorage()),
        external_company_id: '',
        peppol_participant_id: '',
        notes: '',
        api_key: '',
      });
    }
  }, [connections, form]);

  const saveMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      complianceService.upsertAspConnection({
        provider_code: 'aigentrix',
        ...values,
        api_key: values.api_key ? values.api_key : undefined,
      }),
    onSuccess: () => {
      message.success('ASP connection saved');
      queryClient.invalidateQueries({ queryKey: ['asp-connections'] });
    },
    onError: (err: any) => {
      message.error(err?.response?.data?.message || err?.message || 'Save failed');
    },
  });

  const testMutation = useMutation({
    mutationFn: (id: string) => complianceService.testAspConnection(id),
    onSuccess: () => message.success('Aigentrix API reachable'),
    onError: (err: any) =>
      message.error(err?.response?.data?.message || err?.message || 'Connection test failed'),
  });

  const webhookMutation = useMutation({
    mutationFn: async (id: string) => {
      const webhookUrl = String(form.getFieldValue('webhook_url') || '').trim();
      if (!webhookUrl) {
        throw new Error('Enter and save the public webhook URL first');
      }
      // Persist URL then bootstrap
      await complianceService.upsertAspConnection({
        provider_code: 'aigentrix',
        environment: form.getFieldValue('environment'),
        api_base_url: form.getFieldValue('api_base_url'),
        external_company_id: form.getFieldValue('external_company_id'),
        peppol_participant_id: form.getFieldValue('peppol_participant_id'),
        webhook_url: webhookUrl,
        enabled: form.getFieldValue('enabled'),
        notes: form.getFieldValue('notes'),
      });
      return complianceService.bootstrapAspWebhook(id, { webhookUrl });
    },
    onSuccess: () => {
      message.success('Webhook subscription created — complete verify handshake if needed');
      queryClient.invalidateQueries({ queryKey: ['asp-connections'] });
    },
    onError: (err: any) =>
      message.error(err?.response?.data?.message || err?.message || 'Webhook bootstrap failed'),
  });

  return (
    <Card title="ASP connection — Aigentrix (TronStride FZC)" loading={isLoading} style={{ marginBottom: 24 }}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Configure ASP here (not via server environment)"
        description="API base URL (usually https://app.aigentrix.ai), company ID, API key, Peppol participant ID, environment, and webhook URL are stored per company after FTA + Aigentrix portal onboarding. Enable this connection to route Generate UAE E-Invoice through Aigentrix (createFull with status SUBMITTED). API keys are encrypted at rest and never returned to the browser. Full Peppol/FTA cycle can take 1–14 minutes — use webhooks or Refresh ASP."
      />
      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => saveMutation.mutate(values)}
      >
        <Form.Item
          label="Enable Aigentrix ASP"
          name="enabled"
          valuePropName="checked"
          getValueFromEvent={(checked: boolean) => {
            if (checked) {
              // Defer so Form commits `enabled` first, then fill empty defaults.
              queueMicrotask(() => applyAigentrixDefaults(form));
            }
            return checked;
          }}
          extra="Off = local readiness only (uae-fta). On = submit via this ASP. Turning on fills empty defaults (API base, SANDBOX, webhook URL)."
        >
          <Switch />
        </Form.Item>
        <Form.Item label="Environment" name="environment" rules={[{ required: true }]}>
          <Select
            options={[
              { value: 'SANDBOX', label: 'SANDBOX' },
              { value: 'LIVE', label: 'LIVE' },
            ]}
          />
        </Form.Item>
        <Form.Item
          label="API base URL"
          name="api_base_url"
          rules={[
            {
              required: !!enabled,
              message: 'Required when ASP is enabled',
            },
          ]}
          extra={`Default when enabled: ${AIGENTRIX_DEFAULT_API_BASE}`}
        >
          <Input placeholder={AIGENTRIX_DEFAULT_API_BASE} />
        </Form.Item>
        <Form.Item
          label="Aigentrix company ID"
          name="external_company_id"
          rules={[{ required: !!enabled, message: 'Required when ASP is enabled' }]}
        >
          <Input placeholder="Numeric companyId from Aigentrix" />
        </Form.Item>
        <Form.Item label="Peppol participant ID" name="peppol_participant_id">
          <Input placeholder="0235:… or 0088:…" />
        </Form.Item>
        <Form.Item
          label="API key"
          name="api_key"
          rules={
            enabled && !active?.has_api_key
              ? [{ required: true, message: 'API key required when enabling' }]
              : undefined
          }
          extra={
            active?.has_api_key ? (
              <Text type="secondary">Key on file — leave blank to keep existing</Text>
            ) : (
              'Required to enable'
            )
          }
        >
          <Input.Password placeholder="ak_live_… / sandbox key" autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          label="Public webhook URL"
          name="webhook_url"
          extra="HTTPS URL Aigentrix will call. Auto-filled from this company’s ORDAN API path when you enable ASP."
        >
          <Input placeholder="https://…/api/v1/webhooks/aigentrix/{companyId}" />
        </Form.Item>
        <Form.Item label="Notes" name="notes">
          <Input.TextArea rows={2} />
        </Form.Item>
        {active && (
          <Alert
            style={{ marginBottom: 16 }}
            type={active.webhook_status === 'ACTIVE' ? 'success' : 'warning'}
            message={`Webhook status: ${active.webhook_status || 'not configured'}${
              active.webhook_subscription_id ? ` (${active.webhook_subscription_id})` : ''
            }`}
          />
        )}
        <Space wrap>
          <Button type="primary" htmlType="submit" loading={saveMutation.isPending}>
            Save ASP connection
          </Button>
          {active?.id && (
            <Button
              onClick={() => testMutation.mutate(active.id)}
              loading={testMutation.isPending}
              disabled={!active.has_api_key}
            >
              Test connection
            </Button>
          )}
          {active?.id && (
            <Button
              onClick={() => webhookMutation.mutate(active.id)}
              loading={webhookMutation.isPending}
              disabled={!active.has_api_key}
            >
              Bootstrap webhooks
            </Button>
          )}
        </Space>
      </Form>
    </Card>
  );
};

export default AspConnectionPanel;
